import React, { useEffect } from "react";
import { Container, Box, Typography, Card, CardContent } from "@mui/material";
import { updateMetaTags } from "../utils/seo";
import Footer from "../components/Footer";

import YouTubeIcon from "@mui/icons-material/YouTube";

interface Video {
  id: string;
  title: string;
  description: string;
}

const videos: Video[] = [
  {
    id: "4ppJ4sPEyZs",
    title: "A web framework in dart - Jaspr",
    description:
      "In this video, we set up and run our first web server using Jaspr from scratch.",
  },
  {
    id: "0n4FsAKHfNQ",
    title: "Day 2 with the Jaspr Web Framework",
    description:
      "Continuing my hands-on tryout with Jaspr. This video includes real-time exploration, thinking pauses, and a few long silences as I figure things out. I believe this will get smoother with time, and I’m keeping it raw and honest for anyone learning alongside me.",
  },
  {
    id: "DuOkcDm9NyM",
    title: "Calling C from Dart: Dynamic Libraries Explained",
    description:
      "In this video, we explore the basics of Dart FFI by building a simple C dynamic library and using it directly from Dart.We walk through how C code is compiled into a shared library and how Dart loads and calls native functions using FFI.\n\nThis is an introductory, hands-on session focused on understanding the concepts rather than performance or complex use cases.",
  },
  {
    id: "A30FTNwP4yc",
    title: "I Tried raylib for the First Time (Installation + First Drawings)",
    description:
      "Today I’m trying raylib for the very first time.\nI start by:\nInstalling the raylib library (with a few struggles)\nOpening my first window\nDrawing basic shapes on the screen\n\nThis is a beginner’s perspective, so if you’re also new to raylib, you’ll probably relate.\nLet’s learn together\n#raylib #coding #c",
  },
  {
    id: "jLQc4C4OZEw",
    title: "Hello World in dart relic.",
    description:
      "In this video, I’m trying out the Relic library in Dart for the very first time",
  },
  // Add more videos here
];

const YouTube: React.FC = () => {
  useEffect(() => {
    updateMetaTags({
      title: "Shashi Sharma - YouTube Channel | Programming Frameworks",
      description:
        "Watch my YouTube videos about programming frameworks, software development, and technology tutorials",
      keywords:
        "youtube, programming tutorials, software development, programming frameworks, coding videos, tech tutorials, flutter, react, development videos",
      canonical: `${window.location.origin}/youtube`,
      ogTitle: "Shashi Sharma YouTube Channel - Programming Frameworks",
      ogDescription:
        "Watch programming tutorials and framework guides on my YouTube channel",
      ogUrl: `${window.location.origin}/youtube`,
      ogType: "website",
      twitterCard: "summary_large_image",
      twitterTitle: "Shashi Sharma YouTube Channel",
      twitterDescription:
        "Programming frameworks and software development tutorials",
    });
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          py: { xs: 4, md: 6 },
          background: "linear-gradient(135deg, #c31432 0%, #e60023 100%)",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="body1"
            sx={{ color: "rgba(255,255,255,0.95)", mb: 2, textAlign: "center" }}
          >
            Hey! I share raw, unedited coding sessions on my YouTube channel
            where I work through real programming problems and build actual
            projects. No fancy editing, just pure coding and problem-solving in
            real-time.
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "rgba(255,255,255,0.95)", mb: 3, textAlign: "center" }}
          >
            These videos show the actual development process - including
            mistakes, debugging, and figuring things out as I go. It's a more
            realistic look at programming than polished tutorials, and hopefully
            helps you learn how developers actually work.
          </Typography>

          <Box sx={{ textAlign: "center" }}>
            <a
              href="https://www.youtube.com/@_shashisharma"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-block",
                padding: "12px 32px",
                backgroundColor: "white",
                color: "#c31432",
                textDecoration: "none",
                borderRadius: "4px",
                fontWeight: 600,
                transition: "all 0.3s ease",
              }}
            >
              Visit YouTube Channel
            </a>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 6, flex: 1 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 3,
          }}
        >
          {videos.map((video, index) => (
            <Card
              key={index}
              sx={{
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 6,
                },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  paddingTop: "56.25%", // 16:9 aspect ratio
                  backgroundColor: "#000",
                }}
              >
                <iframe
                  src={`https://www.youtube.com/embed/${video.id}`}
                  title={video.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                />
              </Box>
              <CardContent>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {video.title}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  {video.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        {videos.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
            }}
          >
            <YouTubeIcon
              sx={{ fontSize: 100, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h5" color="text.secondary" gutterBottom>
              Videos Coming Soon!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              I'm working on recording more coding sessions. Subscribe to the
              channel to get notified when I upload new videos.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Check out my blog articles for written content in the meantime.
            </Typography>
          </Box>
        )}
      </Container>

      <Footer />
    </Box>
  );
};

export default YouTube;
